/**
 * Canonical Allegiant Pay Period & Payroll Calendar Utility (Client)
 *
 * Allegiant payroll is biweekly.
 * Authoritative anchor:
 *   - Service Period: Saturday through the second Friday (14 days)
 *   - Cutoff Date: Friday 09/25/2026
 *   - Payroll Processing Date: Following Tuesday 09/29/2026
 *   - Employee / Provider Pay Date: Following Wednesday 09/30/2026
 *
 * All past and future pay periods are computed deterministically from this 14-day anchor cycle.
 */

import { assessGuardianPayWindow } from './guardianEncounterDeadline';
import {
  OWNER_DOCUMENTATION_EXCEPTION_LABEL,
  OWNER_NON_BILLABLE_PAY_LABEL,
  isOwnerNonBillableDisposition,
  readOwnerDocumentationException
} from './ownerDocumentationException';

export interface AllegiantPayPeriod {
  id: string; // e.g. "2026-09-12_2026-09-25"
  startDate: string; // YYYY-MM-DD (Saturday)
  endDate: string; // YYYY-MM-DD (Friday, the cutoff)
  cutoffDate: string; // YYYY-MM-DD (Friday, the cutoff)
  processingDate: string; // YYYY-MM-DD (following Tuesday)
  payDate: string; // YYYY-MM-DD (following Wednesday)
  label: string; // e.g. "09/12/2026 – 09/25/2026"
  isCurrent?: boolean;
}

export const ANCHOR_START = '2026-09-12'; // Saturday
export const ANCHOR_CUTOFF = '2026-09-25'; // Friday
export const ANCHOR_PROCESS = '2026-09-29'; // Tuesday
export const ANCHOR_PAY = '2026-09-30'; // Wednesday
export const PAYROLL_TIME_ZONE = 'America/Phoenix';

/** Same America/Phoenix calendar date the server uses for period selection. */
export function payrollCalendarDate(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PAYROLL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (!year || !month || !day) {
    throw new Error('Payroll calendar date could not be resolved in America/Phoenix.');
  }
  return `${year}-${month}-${day}`;
}

export function payrollTodayYmd(): string {
  return payrollCalendarDate(new Date());
}

/**
 * First canonical period that uses 14-day averaging: 2026-10-24 through 2026-11-06.
 * Must match server/payPeriodUtils.ts. Earlier periods keep the Friday cutoff.
 */
export const PAYROLL_AVERAGING_EFFECTIVE_START = '2026-10-24';

export function payrollAveragingApplies(periodStartDate: string): boolean {
  return String(periodStartDate || '') >= PAYROLL_AVERAGING_EFFECTIVE_START;
}

export function payrollPeriodIsComplete(endDate: string, todayYmd = payrollTodayYmd()): boolean {
  return String(todayYmd || '') > String(endDate || '');
}

export function canFinalizePayPeriod(
  period: { startDate: string; endDate: string },
  todayYmd = payrollTodayYmd()
): boolean {
  if (payrollAveragingApplies(period.startDate)) return payrollPeriodIsComplete(period.endDate, todayYmd);
  return String(todayYmd || '') >= String(period.endDate || '');
}

const MS_PER_DAY = 86400000;
const ANCHOR_START_MS = Date.UTC(2026, 8, 12); // September 12, 2026

function parseDateOnly(str: string | Date): { y: number; m: number; d: number } {
  if (str instanceof Date) {
    const [yStr, mStr, dStr] = payrollCalendarDate(str).split('-');
    return { y: parseInt(yStr, 10), m: parseInt(mStr, 10), d: parseInt(dStr, 10) };
  }
  const datePart = str.split('T')[0];
  const [yStr, mStr, dStr] = datePart.split('-');
  return {
    y: parseInt(yStr, 10),
    m: parseInt(mStr, 10),
    d: parseInt(dStr, 10)
  };
}

function formatUtcYmd(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDisplayDate(ymd: string): string {
  if (!ymd || !ymd.includes('-')) return ymd;
  const [y, m, d] = ymd.split('-');
  return `${m}/${d}/${y}`;
}

/**
 * Deterministically returns the Allegiant pay period for any given date of service (DOS).
 */
export function getPayPeriodForDate(input?: string | Date | null): AllegiantPayPeriod {
  const targetYmd = !input
    ? payrollTodayYmd()
    : (input instanceof Date ? payrollCalendarDate(input) : String(input).split('T')[0]);
  const { y, m, d } = parseDateOnly(targetYmd);
  const targetMs = Date.UTC(y, m - 1, d);

  // Calculate day difference from anchor start
  const diffDays = Math.floor((targetMs - ANCHOR_START_MS) / MS_PER_DAY);
  const periodIndex = Math.floor(diffDays / 14);

  const startMs = ANCHOR_START_MS + periodIndex * 14 * MS_PER_DAY;
  const endMs = startMs + 13 * MS_PER_DAY;
  const processMs = endMs + 4 * MS_PER_DAY;
  const payMs = endMs + 5 * MS_PER_DAY;

  const startDate = formatUtcYmd(startMs);
  const endDate = formatUtcYmd(endMs);
  const cutoffDate = endDate;
  const processingDate = formatUtcYmd(processMs);
  const payDate = formatUtcYmd(payMs);

  const todayYmd = payrollTodayYmd();
  const isCurrent = todayYmd >= startDate && todayYmd <= endDate;

  return {
    id: `${startDate}_${endDate}`,
    startDate,
    endDate,
    cutoffDate,
    processingDate,
    payDate,
    label: `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`,
    isCurrent
  };
}

/**
 * Returns the current active Allegiant pay period.
 */
export function getCurrentPayPeriod(): AllegiantPayPeriod {
  return getPayPeriodForDate();
}

/**
 * Returns the adjacent pay period (offset = -1 for previous, +1 for next).
 */
export function getAdjacentPayPeriod(period: AllegiantPayPeriod, offset: number): AllegiantPayPeriod {
  const { y, m, d } = parseDateOnly(period.startDate);
  const startMs = Date.UTC(y, m - 1, d) + offset * 14 * MS_PER_DAY;
  return getPayPeriodForDate(formatUtcYmd(startMs));
}

/**
 * Generates a list of consecutive pay periods centered around the current period.
 */
export function getRecentPayPeriods(countBefore = 4, countAfter = 4): AllegiantPayPeriod[] {
  const current = getCurrentPayPeriod();
  const periods: AllegiantPayPeriod[] = [];
  for (let i = -countBefore; i <= countAfter; i++) {
    periods.push(getAdjacentPayPeriod(current, i));
  }
  return periods;
}

/**
 * Returns true if a given date string (YYYY-MM-DD) falls within the specified pay period.
 */
export function isDateInPayPeriod(dateStr: string | undefined | null, period: AllegiantPayPeriod): boolean {
  if (!dateStr) return false;
  const cleanDate = dateStr.slice(0, 10);
  return cleanDate >= period.startDate && cleanDate <= period.endDate;
}

export interface PayPeriodWeekInfo {
  weekNumber: 1 | 2;
  startDate: string;
  endDate: string;
  label: string;
}

/**
 * Partitions a 14-day Allegiant pay period into its two authoritative 7-day calendar weeks:
 * Week 1: Saturday (Day 0) to Friday (Day 6)
 * Week 2: Saturday (Day 7) to Cutoff Friday (Day 13)
 */
export function getPayPeriodWeeks(period: AllegiantPayPeriod | { startDate: string; endDate: string }): {
  week1: PayPeriodWeekInfo;
  week2: PayPeriodWeekInfo;
} {
  const { y, m, d } = parseDateOnly(period.startDate);
  const startMs = Date.UTC(y, m - 1, d);

  const w1StartMs = startMs;
  const w1EndMs = startMs + 6 * MS_PER_DAY;
  const w2StartMs = startMs + 7 * MS_PER_DAY;
  const w2EndMs = startMs + 13 * MS_PER_DAY;

  const w1Start = formatUtcYmd(w1StartMs);
  const w1End = formatUtcYmd(w1EndMs);
  const w2Start = formatUtcYmd(w2StartMs);
  const w2End = formatUtcYmd(w2EndMs);

  return {
    week1: {
      weekNumber: 1,
      startDate: w1Start,
      endDate: w1End,
      label: `Week 1 (${formatDisplayDate(w1Start)} – ${formatDisplayDate(w1End)})`
    },
    week2: {
      weekNumber: 2,
      startDate: w2Start,
      endDate: w2End,
      label: `Week 2 (${formatDisplayDate(w2Start)} – ${formatDisplayDate(w2End)})`
    }
  };
}

/**
 * Allegiant Authoritative Weekly Completed Session Rate Ladder
 *
 * 1 completed session = $25/session
 * 2 = $40/session
 * 3 = $50/session
 * 4–6 = $60/session
 * 7–9 = $65/session
 * 10–19 = $70/session
 * 20–24 = $75/session
 * 25–29 = $80/session
 * 30+ = $85/session
 */
export function getStandardWeeklySessionRate(completedCount: number): number {
  if (completedCount <= 0) return 0;
  if (completedCount === 1) return 25;
  if (completedCount === 2) return 40;
  if (completedCount === 3) return 50;
  if (completedCount <= 6) return 60;
  if (completedCount <= 9) return 65;
  if (completedCount <= 19) return 70;
  if (completedCount <= 24) return 75;
  if (completedCount <= 29) return 80;
  return 85;
}

export type TreatmentPayLadder = 'STANDARD' | 'CF_PLUS_5';

export function normalizeTreatmentPayLadder(value?: string | null): TreatmentPayLadder {
  return String(value || '').trim().toUpperCase() === 'CF_PLUS_5' ? 'CF_PLUS_5' : 'STANDARD';
}

export function getWeeklySessionRate(completedCount: number, ladder?: string | null): number {
  const base = getStandardWeeklySessionRate(completedCount);
  if (base <= 0) return 0;
  return normalizeTreatmentPayLadder(ladder) === 'CF_PLUS_5' ? base + 5 : base;
}

export interface NextLadderTierInfo {
  nextTierCount: number | null;
  sessionsToNextTier: number | null;
  nextTierRate: number | null;
}

export function getNextLadderTierInfo(currentCount: number, ladder?: string | null): NextLadderTierInfo {
  const bump = normalizeTreatmentPayLadder(ladder) === 'CF_PLUS_5' ? 5 : 0;
  if (currentCount <= 0) return { nextTierCount: 1, sessionsToNextTier: 1, nextTierRate: 25 + bump };
  if (currentCount === 1) return { nextTierCount: 2, sessionsToNextTier: 1, nextTierRate: 40 + bump };
  if (currentCount === 2) return { nextTierCount: 3, sessionsToNextTier: 1, nextTierRate: 50 + bump };
  if (currentCount === 3) return { nextTierCount: 4, sessionsToNextTier: 1, nextTierRate: 60 + bump };
  if (currentCount <= 6) return { nextTierCount: 7, sessionsToNextTier: 7 - currentCount, nextTierRate: 65 + bump };
  if (currentCount <= 9) return { nextTierCount: 10, sessionsToNextTier: 10 - currentCount, nextTierRate: 70 + bump };
  if (currentCount <= 19) return { nextTierCount: 20, sessionsToNextTier: 20 - currentCount, nextTierRate: 75 + bump };
  if (currentCount <= 24) return { nextTierCount: 25, sessionsToNextTier: 25 - currentCount, nextTierRate: 80 + bump };
  if (currentCount <= 29) return { nextTierCount: 30, sessionsToNextTier: 30 - currentCount, nextTierRate: 85 + bump };
  return { nextTierCount: null, sessionsToNextTier: null, nextTierRate: null };
}

export interface CanonicalServiceCategory {
  serviceType: 'TREATMENT' | 'EVALUATION' | 'UNKNOWN';
  modality: 'ONSITE' | 'TELETHERAPY' | 'AMBIGUOUS';
  serviceCode: string;
  isAmbiguous: boolean;
  ambiguityReason?: string;
}

export function resolveServiceCategory(service: {
  service_code?: string;
  doc_type?: string;
  auth_type?: string;
  place_of_service?: string;
  modifier?: string;
}): CanonicalServiceCategory {
  const code = (service.service_code || '').trim();
  const docType = (service.doc_type || '').toUpperCase().trim();
  const authType = (service.auth_type || '').toUpperCase().trim();
  const pos = (service.place_of_service || '').trim();
  const modifier = (service.modifier || '').toUpperCase().trim();

  let serviceType: 'TREATMENT' | 'EVALUATION' | 'UNKNOWN' = 'UNKNOWN';
  if (
    ['92523', '92521', '92522', '92524', '92610'].includes(code) ||
    ['EVAL', 'EVALUATION'].includes(docType) ||
    ['SEA', 'FEA'].includes(authType)
  ) {
    serviceType = 'EVALUATION';
  } else if (
    ['92507', '92526'].includes(code) ||
    ['SOAP', 'TREATMENT'].includes(docType) ||
    ['STA', 'FTA'].includes(authType)
  ) {
    serviceType = 'TREATMENT';
  }

  let modality: 'ONSITE' | 'TELETHERAPY' | 'AMBIGUOUS' = 'ONSITE';
  if (['02', '10'].includes(pos) || ['95', 'GT'].includes(modifier)) {
    modality = 'TELETHERAPY';
  } else if (['11', '12', '03', '99'].includes(pos) || (!pos && !modifier)) {
    modality = 'ONSITE';
  } else if (pos) {
    modality = 'ONSITE';
  } else {
    modality = 'AMBIGUOUS';
  }

  const isAmbiguous = serviceType === 'UNKNOWN' || modality === 'AMBIGUOUS';
  const ambiguityReason = isAmbiguous
    ? `Ambiguous encounter: service code '${code}', doc_type '${docType}', POS '${pos}', modifier '${modifier}'.`
    : undefined;

  return {
    serviceType,
    modality,
    serviceCode: code,
    isAmbiguous,
    ambiguityReason
  };
}

export interface ResolvedCompensation {
  serviceType: 'TREATMENT' | 'EVALUATION' | 'UNKNOWN';
  modality: 'ONSITE' | 'TELETHERAPY' | 'AMBIGUOUS';
  baseRate: number;
  rateSource: string;
  isCustomOverride: boolean;
  plannedMinutes: number;
  actualMinutes: number;
  prorationFactor: number;
  earnedPay: number;
  isHoldAmbiguous: boolean;
  holdReason?: string;
  isComplianceOverride?: boolean;
  isUnder53Minutes?: boolean;
  isLateGuardianSignature?: boolean;
  isOwnerDocumentationException?: boolean;
  isOwnerNonBillable?: boolean;
  encounterClockMissing?: boolean;
  encounterClockReason?: string;
  guardianWindowStart?: string;
  guardianCompletedAt?: string;
  guardianElapsedHours?: number;
}

/**
 * Authoritative Payroll Rate Resolution Order:
 *
 * For Treatment / SOAPs:
 * 1. Treatment Compliance Override ($25 flat earned pay):
 *    Triggered if actual treatment duration < 53 minutes OR required guardian signature
 *    was completed after the Arizona session end plus 24 hours, or is still unsigned after that deadline.
 *    If both conditions occur, pay remains $25 without stacking.
 *    Precedence: Compliance override supersedes custom/legacy treatment rate, teletherapy $40, and weekly ladder.
 * 2. Otherwise: Provider custom/legacy treatment override if configured (providers.rate_per_session > 0)
 * 3. Otherwise: Teletherapy treatment -> $40 flat
 * 4. Otherwise: Onsite treatment -> weekly volume ladder tier (STANDARD, or CF_PLUS_5 = standard + $5)
 * Duration proration (actual / planned) applies to non-compliance treatment. Manual adjustments applied separately.
 *
 * For Evaluations:
 * 1. Provider custom/legacy eval override if configured (providers.rate_per_eval > 0)
 * 2. Otherwise: Teletherapy evaluation -> $150 flat
 * 3. Otherwise: Onsite evaluation -> $175 flat
 * Evaluations are flat: NO 53-minute minimum, NO duration proration, and NEVER subject to treatment compliance rate.
 *
 * Manual adjustments applied separately afterward for all services.
 */
export function resolveServiceCompensation(
  service: any,
  provider: { rate_per_session?: number | null; rate_per_eval?: number | null; pay_ladder?: string | null },
  weekOnsiteTreatmentCount: number
): ResolvedCompensation {
  const cat = resolveServiceCategory(service);

  const planned = Number(service.planned_duration_minutes || service.duration_minutes || 60);
  const actual = Number(service.actual_duration_minutes || service.duration_minutes || planned);

  if (cat.isAmbiguous) {
    return {
      serviceType: cat.serviceType,
      modality: cat.modality,
      baseRate: 0,
      rateSource: 'HOLD_AMBIGUOUS_SERVICE',
      isCustomOverride: false,
      plannedMinutes: planned,
      actualMinutes: actual,
      prorationFactor: 0,
      earnedPay: 0,
      isHoldAmbiguous: true,
      holdReason: cat.ambiguityReason
    };
  }

  let baseRate = 0;
  let rateSource = '';
  let isCustomOverride = false;
  let prorationFactor = 1.0;
  let isComplianceOverride = false;
  let isUnder53Minutes = false;
  let isLateGuardianSignature = false;
  let isOwnerDocumentationException = false;
  let isOwnerNonBillable = false;
  let encounterClockMissing = false;
  let encounterClockReason: string | undefined = undefined;
  let guardianWindowStart: string | undefined = undefined;
  let guardianCompletedAt: string | undefined = undefined;
  let guardianElapsedHours: number | undefined = undefined;

  if (cat.serviceType === 'TREATMENT') {
    const guardianWindow = assessGuardianPayWindow(service);
    isLateGuardianSignature = guardianWindow.isLate;
    guardianWindowStart = guardianWindow.sessionEndIso;
    guardianCompletedAt = guardianWindow.completedAt;
    guardianElapsedHours = guardianWindow.elapsedHours;

    // 2. Check 53-minute minimum duration rule
    if (actual < 53) {
      isUnder53Minutes = true;
    }

    // 3. Treatment Compliance Override Check
    // The owner exception is the same flat $25. It does not mark the signature late.
    const ownerException = readOwnerDocumentationException(service.pay_calculation_notes).active
      || service.ownerDocumentationException === true;
    const ownerClosed = isOwnerNonBillableDisposition(service);
    if (isUnder53Minutes || isLateGuardianSignature || ownerException || ownerClosed) {
      isComplianceOverride = true;
      isOwnerDocumentationException = ownerException;
      isOwnerNonBillable = ownerClosed;
      baseRate = 25.0;
      prorationFactor = 1.0;
      if (ownerClosed) {
        rateSource = OWNER_NON_BILLABLE_PAY_LABEL;
      } else if (ownerException) {
        rateSource = OWNER_DOCUMENTATION_EXCEPTION_LABEL;
      } else if (isUnder53Minutes && isLateGuardianSignature) {
        rateSource = 'Compliance Rate — Duration + Late Signature — $25';
      } else if (isUnder53Minutes) {
        rateSource = 'Compliance Rate — Under 53 Minutes — $25';
      } else {
        rateSource = 'Compliance Rate — Guardian Signature >24 Hours — $25';
      }
    } else {
      // Normal Treatment Rate Resolution:
      const hasCustomTreatment =
        provider.rate_per_session !== null &&
        provider.rate_per_session !== undefined &&
        Number(provider.rate_per_session) > 0;

      if (hasCustomTreatment) {
        baseRate = Number(provider.rate_per_session);
        rateSource = `Custom / Legacy Rate — $${baseRate}`;
        isCustomOverride = true;
      } else if (cat.modality === 'TELETHERAPY') {
        baseRate = 40.0;
        rateSource = 'Teletherapy Treatment — $40';
      } else {
        const payLadder = normalizeTreatmentPayLadder(provider.pay_ladder);
        baseRate = getWeeklySessionRate(weekOnsiteTreatmentCount, payLadder);
        rateSource = payLadder === 'CF_PLUS_5'
          ? `CF Volume Tier — ${weekOnsiteTreatmentCount} Sessions — $${baseRate}`
          : `Standard Volume Tier — ${weekOnsiteTreatmentCount} Sessions — $${baseRate}`;
      }

      const portion = planned > 0 ? actual / planned : 1.0;
      prorationFactor = Math.min(portion, 1.0);
      if (guardianWindow.missing || guardianWindow.unreadableSignedAt) {
        encounterClockMissing = true;
        encounterClockReason = guardianWindow.reason;
        baseRate = 0;
        rateSource = guardianWindow.reason || 'Encounter date and session end time required';
        prorationFactor = 0;
        isCustomOverride = false;
      }
    }
  } else if (cat.serviceType === 'EVALUATION') {
    const hasCustomEval =
      provider.rate_per_eval !== null &&
      provider.rate_per_eval !== undefined &&
      Number(provider.rate_per_eval) > 0;

    if (hasCustomEval) {
      baseRate = Number(provider.rate_per_eval);
      rateSource = `Custom / Legacy Rate — $${baseRate}`;
      isCustomOverride = true;
    } else if (cat.modality === 'TELETHERAPY') {
      baseRate = 150.0;
      rateSource = 'Teletherapy Evaluation — $150';
    } else {
      baseRate = 175.0;
      rateSource = 'Onsite Evaluation — $175';
    }

    prorationFactor = 1.0;
  }

  const earnedPay =
    isComplianceOverride
      ? 25.0
      : baseRate > 0
      ? Math.round(baseRate * prorationFactor * 100) / 100
      : 0;

  return {
    serviceType: cat.serviceType,
    modality: cat.modality,
    baseRate,
    rateSource,
    isCustomOverride,
    plannedMinutes: planned,
    actualMinutes: actual,
    prorationFactor: Math.round(prorationFactor * 1000) / 1000,
    earnedPay,
    isHoldAmbiguous: false,
    isComplianceOverride,
    isUnder53Minutes,
    isLateGuardianSignature,
    isOwnerDocumentationException,
    isOwnerNonBillable,
    encounterClockMissing,
    encounterClockReason,
    guardianWindowStart,
    guardianCompletedAt,
    guardianElapsedHours: guardianElapsedHours !== undefined ? Math.round(guardianElapsedHours * 10) / 10 : undefined
  };
}

// --- CANONICAL PERIODICITY MODES & HIERARCHY RESOLUTION ---

export type CanonicalPeriodMode = 'PAY_PERIOD' | 'WEEK' | 'MONTH' | 'QUARTER' | 'CUSTOM' | 'ALL_TIME';

export interface ResolvedPeriodContext {
  periodType: CanonicalPeriodMode;
  startDate: string; // YYYY-MM-DD or empty for ALL_TIME
  endDate: string; // YYYY-MM-DD or empty for ALL_TIME
  periodLabel: string;
  payPeriod?: AllegiantPayPeriod;
  weekNumber?: 1 | 2;
}

/**
 * Canonical Document Date Basis Resolution:
 * 1. Encounter DOS (s.dos)
 * 2. Document Date (d.document_date)
 * 3. Fallback created_at date (d.created_at YYYY-MM-DD)
 */
export function getCanonicalDocumentDate(doc: {
  dos?: string | null;
  document_date?: string | null;
  created_at?: string | null;
}): string {
  if (doc.dos && typeof doc.dos === 'string' && doc.dos.trim()) {
    return doc.dos.trim().slice(0, 10);
  }
  if (doc.document_date && typeof doc.document_date === 'string' && doc.document_date.trim()) {
    return doc.document_date.trim().slice(0, 10);
  }
  if (doc.created_at && typeof doc.created_at === 'string' && doc.created_at.trim()) {
    return doc.created_at.trim().slice(0, 10);
  }
  return '';
}

/**
 * Resolves a canonical period based on mode and reference pay period or custom dates.
 */
export function resolveCanonicalPeriod(
  mode: CanonicalPeriodMode,
  options?: {
    payPeriod?: AllegiantPayPeriod;
    weekNumber?: 1 | 2;
    customStartDate?: string;
    customEndDate?: string;
    targetDate?: string | Date;
  }
): ResolvedPeriodContext {
  const currentPayPeriod = options?.payPeriod || getCurrentPayPeriod();
  const refDateStr = currentPayPeriod.startDate;
  const { y, m } = parseDateOnly(options?.targetDate || refDateStr);

  switch (mode) {
    case 'PAY_PERIOD': {
      return {
        periodType: 'PAY_PERIOD',
        startDate: currentPayPeriod.startDate,
        endDate: currentPayPeriod.endDate,
        periodLabel: `${formatDisplayDate(currentPayPeriod.startDate)} – ${formatDisplayDate(currentPayPeriod.endDate)}`,
        payPeriod: currentPayPeriod
      };
    }
    case 'WEEK': {
      const weeks = getPayPeriodWeeks(currentPayPeriod);
      const chosenWeek = options?.weekNumber === 2 ? weeks.week2 : weeks.week1;
      return {
        periodType: 'WEEK',
        startDate: chosenWeek.startDate,
        endDate: chosenWeek.endDate,
        periodLabel: chosenWeek.label,
        payPeriod: currentPayPeriod,
        weekNumber: chosenWeek.weekNumber
      };
    }
    case 'MONTH': {
      const firstDay = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDayMs = Date.UTC(y, m, 0);
      const lastDay = formatUtcYmd(lastDayMs);
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthLabel = `${monthNames[m - 1]} ${y}`;
      return {
        periodType: 'MONTH',
        startDate: firstDay,
        endDate: lastDay,
        periodLabel: `${monthLabel} (${formatDisplayDate(firstDay)} – ${formatDisplayDate(lastDay)})`,
        payPeriod: currentPayPeriod
      };
    }
    case 'QUARTER': {
      const qNum = Math.floor((m - 1) / 3) + 1;
      const qStartMonth = (qNum - 1) * 3 + 1;
      const qEndMonth = qNum * 3;
      const firstDay = `${y}-${String(qStartMonth).padStart(2, '0')}-01`;
      const lastDayMs = Date.UTC(y, qEndMonth, 0);
      const lastDay = formatUtcYmd(lastDayMs);
      return {
        periodType: 'QUARTER',
        startDate: firstDay,
        endDate: lastDay,
        periodLabel: `Q${qNum} ${y} (${formatDisplayDate(firstDay)} – ${formatDisplayDate(lastDay)})`,
        payPeriod: currentPayPeriod
      };
    }
    case 'CUSTOM': {
      const start = options?.customStartDate || currentPayPeriod.startDate;
      const end = options?.customEndDate || currentPayPeriod.endDate;
      return {
        periodType: 'CUSTOM',
        startDate: start,
        endDate: end,
        periodLabel: start && end
          ? `Custom (${formatDisplayDate(start)} – ${formatDisplayDate(end)})`
          : 'Custom Period',
        payPeriod: currentPayPeriod
      };
    }
    case 'ALL_TIME':
    default: {
      return {
        periodType: 'ALL_TIME',
        startDate: '',
        endDate: '',
        periodLabel: 'All Historical Periods',
        payPeriod: currentPayPeriod
      };
    }
  }
}

